import axios, { type AxiosInstance, type AxiosRequestConfig, AxiosError } from 'axios'
import { i18n } from '@/i18n'
import type { CaptchaAnswer, CaptchaChallenge } from '@/utils/captcha'
import type { ABreakdown, AnalyticsQuery, LogPage, OOverview, ORank, XOverview, XRank } from '@/utils/analytics'
import type { Complaint, ComplaintSummary, Ticket, TicketDetail, TicketFile, TicketPriority } from '@/utils/tickets'
import type { ExpLevel } from '@/utils/lvBadge'
import type { LicenseState } from '@/utils/license'

// One definition of the storage key: the request interceptor reads it,
// the 401 handler clears it, and the auth store persists it.
export const TOKEN_KEY = 'cdnx_admin_token'

// HTTP client targeting the ADMIN engine — a separate server/port/JWT
// secret from the customer console (control/internal/config's
// AdminServer/AdminJWT). The base URL is /api so it proxies through Vite
// in dev and is served by the same nginx (fronting the admin engine's
// port) in prod. This client must never be pointed at the customer
// engine — the two consoles intentionally share no trust boundary.
const client: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 30_000,
  withCredentials: false,
})

// Inject the JWT from local storage on every request. Stored under a
// DIFFERENT key than the customer console so the two apps never collide
// if ever served from the same browser profile.
client.interceptors.request.use((config) => {
  const tok = localStorage.getItem(TOKEN_KEY)
  if (tok) {
    config.headers = config.headers ?? {}
    config.headers.Authorization = `Bearer ${tok}`
  }
  return config
})

// Server-side messages arrive as a stable code plus the values the sentence
// interpolates, with the English text alongside as "message". Looking the
// code up in the console's own catalogue is what makes a Chinese operator
// see Chinese guidance: translating on the server would mean carrying the
// request's locale through every service and putting UI text in the domain
// layer.
//
// Falling back to the server's English is deliberate rather than showing a
// raw key: an error nobody has translated yet is still worth reading, and a
// bare "region.codeInUse" on screen would be worse than the sentence it
// replaced.
export function localizeError(code: string | undefined, message: string | undefined, params?: Record<string, unknown>) {
  if (code) {
    const key = `serverErrors.${code}`
    const t = i18n.global.t as (k: string, p?: Record<string, unknown>) => string
    const translated = t(key, params ?? {})
    if (translated !== key) return translated
  }
  return message ?? ''
}

// Centralised error shaping. Handlers always reject with `ApiError`.
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly requestId?: string
  // The server's own English text, kept next to the localized `message` so
  // a bug report can quote what the control plane actually said.
  readonly rawMessage: string
  constructor(status: number, code: string, message: string, requestId?: string, rawMessage?: string) {
    super(message)
    this.status = status
    this.code = code
    this.requestId = requestId
    this.rawMessage = rawMessage ?? message
  }
}

client.interceptors.response.use(
  (r) => r,
  (err: AxiosError<{ code?: string; message?: string; request_id?: string; params?: Record<string, unknown> }>) => {
    if (err.response) {
      const { status, data } = err.response
      // An expired session used to surface as a failed request on every
      // page and nothing else: the console stayed on a screen it could not
      // load until the user happened to reload, which is when the router
      // guard finally noticed. Clearing the token and going to the login
      // page is what the 401 actually means.
      //
      // `window.location` rather than the router on purpose — the router
      // imports the auth store, which imports this module, and reaching
      // back for it here would close that cycle.
      //
      // Only when the request carried the token still held: one sent just
      // before 修改密码 or 退出其他设备 handed this session a new token comes
      // back 401 too, and must not sign the session out.
      const sent = (err.config?.headers as Record<string, unknown> | undefined)?.Authorization
      const held = localStorage.getItem(TOKEN_KEY)
      const stale = !!sent && sent !== `Bearer ${held}`
      if (status === 401 && !stale && !window.location.pathname.startsWith('/login')) {
        localStorage.removeItem(TOKEN_KEY)
        const next = window.location.pathname + window.location.search
        window.location.assign('/login?next=' + encodeURIComponent(next))
      }
      throw new ApiError(
        status,
        data?.code ?? 'HTTP_' + status,
        localizeError(data?.code, data?.message ?? err.message, data?.params),
        data?.request_id,
        data?.message ?? err.message,
      )
    }
    throw new ApiError(0, 'NETWORK', err.message)
  },
)

// Public surface.
// Mirrors control/internal/controltls.Settings (主控通信).
export interface ControlTLSSettings {
  domain: string
  port: number
  mode: 'off' | 'prefer' | 'require'
  email: string
  ca: 'letsencrypt' | 'zerossl'
  source?: '' | 'acme' | 'upload'
}
// Mirrors service.ControlCert.
export interface ControlCert {
  source: string
  common_name: string
  names: string[] | null
  issuer: string
  not_before: string
  not_after: string
  fingerprint: string
  covers_domain: boolean
  trusted: boolean
  trust_error?: string
  expired: boolean
  usable: boolean
}
export interface ControlTLSNode {
  id: number
  name: string
  alias?: string
  ip?: string
  transport: '' | 'http' | 'https'
  endpoint_error?: string
  online: boolean
  last_heartbeat?: string
}
// Mirrors service.CertIssueState.
export interface IssueState {
  state: 'none' | 'pending' | 'success' | 'failed' | 'cancelled'
  attempts: number
  max_attempts: number
  next_run_at?: string
  last_run_at?: string
  reason?: { code?: string; message?: string; params?: Record<string, unknown> }
}
export interface ControlTLSStatus {
  settings: ControlTLSSettings
  agent_url: string
  cert?: ControlCert
  endpoint: { agent_url?: string; require?: boolean }
  key_encrypted: boolean
  acme: boolean
  nodes: ControlTLSNode[]
}

// Mirrors service.ConsoleSettings (主控域名).
export type ConsoleSiteName = 'customer' | 'admin'
export interface ConsoleSite {
  domain: string
  redirect_https: boolean
  source?: '' | 'acme' | 'upload'
}
export interface ConsoleSettings {
  customer: ConsoleSite
  admin: ConsoleSite
  https_port: number
  email: string
  ca: 'letsencrypt' | 'zerossl'
  admin_domain_only: boolean
}
export interface ConsoleTLSStatus {
  settings: ConsoleSettings
  sites: Record<ConsoleSiteName, { url: string; cert?: ControlCert }>
  key_encrypted: boolean
  acme: boolean
}

// Mirrors control/internal/fwpolicy.Policy.
export interface FirewallPolicy {
  scope: 'ports' | 'all'
  tcp: string[]
  udp: string[]
  exempt_tcp: string[]
  exempt_udp: string[]
}

// Mirrors control/internal/service.CaptchaView / CaptchaInput: 人机验证.
export type CaptchaProvider = 'local' | 'tencent' | 'geetest' | 'google'
export type CaptchaLoginMode = 'off' | 'failed' | 'always'
export interface CaptchaState {
  since?: string
  failures: number
  allowed: number
  denied: number
  last_at?: string
  last_error?: string
  last_scene?: string
  last_provider?: string
}
export interface CaptchaView {
  enabled: boolean
  provider: CaptchaProvider
  on_error: 'allow' | 'deny'
  send_code: boolean
  register: boolean
  login: CaptchaLoginMode
  admin_login: CaptchaLoginMode
  login_failures: number
  tencent: { app_id: string; has_app_secret_key: boolean; secret_id: string; has_secret_key: boolean }
  geetest: { captcha_id: string; has_secret_key: boolean }
  google: { site_key: string; has_secret_key: boolean; host: string }
  encryption: boolean
  ready: boolean
  challenge: CaptchaChallenge
  state: CaptchaState
}
export interface CaptchaInput {
  enabled: boolean
  provider: CaptchaProvider
  on_error: 'allow' | 'deny'
  send_code: boolean
  register: boolean
  login: CaptchaLoginMode
  admin_login: CaptchaLoginMode
  login_failures: number
  tencent: { app_id: string; app_secret_key: string; secret_id: string; secret_key: string }
  geetest: { captcha_id: string; secret_key: string }
  google: { site_key: string; secret_key: string; host: string }
}

// Mirrors control/internal/fwpolicy.Sync: 全网联动封禁.
export interface BanSync {
  enabled: boolean
}

// Mirrors control/internal/fwpolicy.Limit and .Protection.
export interface ProtectionLimit {
  enabled: boolean
  threshold: number
  window_seconds: number
  ban_seconds: number
}
// Mirrors control/internal/fwpolicy.BlockPolicy.
export interface BlockPolicy {
  mode: 'page' | 'drop' | 'kernel'
  kernel_ban_seconds: number
  escalate: { enabled: boolean; threshold: number; window_seconds: number }
  surge: { enabled: boolean; threshold: number; duration_seconds: number }
}
export interface NodeProtection {
  conn_rate: ProtectionLimit
  tls_fail: ProtectionLimit
  error_pages: ProtectionLimit
  block: BlockPolicy
  block_cross_site: boolean
  // Mirrors control/internal/fwpolicy.HandshakeCap (握手速率上限).
  handshake: { enabled: boolean; per_core: number; limit: number; known_seconds: number }
}

// Mirrors control/internal/pages.
export type DefaultPageMode = 'default' | 'text' | 'html' | 'drop'
export interface DefaultPage {
  mode: DefaultPageMode
  status: number
  title: string
  message: string
  html: string
}
export interface DefaultPagesConfig {
  pages: Record<string, DefaultPage>
  plan_expiry: { suspend: boolean; grace_hours: number }
}
export interface DefaultPageDef {
  key: string
  status: number
  can_drop: boolean
  show_reason: boolean
  // Serving it counts toward the firewall's error_pages limit.
  counted: boolean
  title_zh: string
  title_en: string
  message_zh: string
  message_en: string
  default_html: string
}

// Mirrors control/internal/service.Subscription.
export type SubscriptionState = 'active' | 'expiring' | 'expired' | 'cancelled'
export interface Subscription {
  id: number
  user_id: number
  package_id: number
  started_at: string
  expires_at: string
  /** 自动续费: renewed from the customer's balance in the last 3 days, for
   *  auto_renew_period ('' is a month); auto_renew_error is the last
   *  automatic renewal's failure (a server error code), '' if none. */
  auto_renew: boolean
  auto_renew_period: '' | 'month' | 'quarter' | 'year'
  auto_renew_error: string
  status: string
  username: string
  package_name: string
  state: SubscriptionState
  sites: number
  usage?: import('@/components/QuotaUsage.vue').QuotaUsageData
}

// Mirrors control/internal/model.ACLRule: a site's (owner_type domain,
// domain_id set) or the platform's (system, global — every site).
export interface AclRule {
  id: number
  owner_type: string
  domain_id?: number
  name: string
  type: string
  match: Record<string, unknown>
  action: string
  status: string
  priority: number
}

// The ClickHouse page (/admin/system/clickhouse). Mirrors
// control/internal/service/clickhouse.go and chconf.
export interface CHConnView {
  addr: string
  database: string
  username: string
  secure: boolean
  password_set: boolean
}
export interface CHState {
  connected: boolean
  since?: string
  error?: string
  version?: string
  uptime?: number
  timezone?: string
}
export interface CHDisk {
  name: string
  path: string
  total: number
  free: number
  pending: number
  used_pct: number
}
export interface CHTable {
  name: string
  managed: boolean
  days?: number
  default_days?: number
  rows: number
  bytes: number
  raw: number
  parts: number
  partitions: number
  oldest?: string
  newest?: string
  ttl: boolean
}
export interface CHUsage {
  tables: number
  rows: number
  bytes: number
}
export interface CHCleanup {
  enabled: boolean
  high_pct: number
  low_pct: number
  keep_days: number
  system_logs: boolean
}
export interface CHDropped {
  table: string
  day?: string
  rows: number
  bytes: number
  why: 'retention' | 'disk' | 'system_log'
}
export interface CHRun {
  at: string
  trigger: 'auto' | 'manual' | 'policy' | 'system_logs'
  dropped: CHDropped[]
  freed: number
  disk_before?: number
  disk_after?: number
  short?: boolean
  error?: string
}
export interface CHCleanupRecord {
  id: number
  at: string
  actor_type: string
  actor_id: string
  run: CHRun
}
export interface CHOverview {
  source: 'console' | 'file'
  conn: CHConnView
  file: CHConnView
  password_sealed: boolean
  state: CHState
  disks: CHDisk[]
  tables: CHTable[]
  system_logs: CHUsage
  other_databases: CHUsage
  missing: string[]
  retention: Record<string, number>
  cleanup: CHCleanup
  last?: CHRun
  history: CHCleanupRecord[]
  problems?: Record<string, string>
}
export interface CHConnInput {
  addr: string
  database: string
  username: string
  /** Left out: the password in use stays. */
  password?: string
  secure: boolean
}
export interface CHTestResult {
  ok: boolean
  version?: string
  latency_ms: number
  database_exists: boolean
  missing: string[]
  error?: string
}

// 套餐功能开关 and 数量配额 — mirrors control/internal/service.SiteEntitlements
// and PackageAllowances. A count's max 0 is no ceiling.
export type Feature = 'waf' | 'custom_cc' | 'websocket' | 'http3' | 'custom_cert' | 'openapi' | 'l4'
export const FEATURES: Feature[] = ['waf', 'custom_cc', 'websocket', 'http3', 'custom_cert', 'openapi', 'l4']
export interface Allowance {
  used: number
  max: number
}
export interface SiteEntitlements {
  package: { id: number; name: string } | null
  /** The caller is held to it — a customer; an operator is not. */
  enforced: boolean
  features: Record<Feature, boolean>
  rules: Allowance
  sites: Allowance
  hostnames: Allowance
  apexes: Allowance
}
export interface PackageAllowances {
  features: Record<Feature, boolean>
  sites: Allowance
  hostnames: Allowance
  apexes: Allowance
  rules_per_site: number
}

// 支付配置 and 充值 — mirrors control/internal/service PaymentView,
// AlipayInput, AlipayTest, BillingSummary, OrderView, OrderCheck,
// AccountView and LedgerView. Every amount is 分.
export interface TopUpSettings {
  /** The customer console's public address: Alipay notifies and returns there. */
  base_url: string
  subject: string
  min_cents: number
  max_cents: number
  presets: number[]
}
export type SignMode = 'key' | 'cert'
export interface AlipayView {
  enabled: boolean
  app_id: string
  sign_mode: SignMode
  sandbox: boolean
  /** The private key itself never comes back. */
  has_private_key: boolean
  alipay_public_key: string
  app_cert: string
  alipay_cert: string
  alipay_root_cert: string
  page: boolean
  wap: boolean
  qr: boolean
  /** Customers can pay through it now. */
  ready: boolean
}
export interface PaymentView {
  topup: TopUpSettings
  alipay: AlipayView
  notify_url: string
  return_url: string
  /** The customer console's HTTPS address when 主控域名 names one. */
  suggested_base_url: string
  /** A key exists to seal the private key with. */
  encryption: boolean
}
export interface AlipayInput {
  enabled: boolean
  app_id: string
  sign_mode: SignMode
  sandbox: boolean
  /** Empty keeps the stored key. */
  private_key: string
  alipay_public_key: string
  app_cert: string
  alipay_cert: string
  alipay_root_cert: string
  page: boolean
  wap: boolean
  qr: boolean
}
export type AlipayTestResult = 'ok' | 'invalidSignature' | 'invalidAppId' | 'answerSignature' | 'unreachable' | 'notReady' | 'other'
export interface AlipayTest {
  result: AlipayTestResult
  detail?: string
  gateway: string
}
export interface BillingSummary {
  paid_total_cents: number
  today_cents: number
  pending_orders: number
  orders: number
}
export type PayMethod = 'page' | 'wap' | 'qr'
export type TopUpState = 'pending' | 'paid' | 'closed' | 'expired'
export interface TopUpOrder {
  id: number
  out_trade_no: string
  user_id: number
  username?: string
  amount_cents: number
  channel: string
  method: PayMethod
  status: string
  state: TopUpState
  trade_no: string
  buyer: string
  paid_at?: string
  expires_at: string
  created_at: string
}
export interface OrderCheck {
  order: TopUpOrder
  /** Alipay's trade state, or NOT_EXIST. */
  trade: string
  credited: boolean
  /** What the buyer paid, when it is not the order's amount: never credited. */
  paid_cents?: number
}
export interface LedgerEntry {
  id: number
  user_id: number
  kind: 'topup' | 'adjust' | 'remittance' | 'withdraw' | 'withdraw_back' | 'refund' | 'refund_back' | 'purchase' | 'order_refund'
    | 'realname'
    | 'realname_back'
  amount_cents: number
  balance_cents: number
  order_id?: number
  order_no?: string
  /** The finance document behind other changes: PO…, RM…, WD…, RF…. */
  ref_no?: string
  actor_id?: number
  actor_name?: string
  note: string
  created_at: string
}
export interface AccountView {
  balance_cents: number
  ledger: LedgerEntry[]
  ledger_total: number
}

// 财务管理 — mirrors control/internal/service FinanceSettings,
// FinanceSummary and the document views (model + username). Every amount
// is 分.
export type Period = 'month' | 'quarter' | 'year'
export type DocStatus = 'pending' | 'processing' | 'success' | 'failed' | 'approved' | 'rejected' | 'cancelled' | 'issued'
export interface BankAccount {
  /** Made by the server on save; empty for a new one. */
  id: string
  name: string
  bank: string
  account: string
  note: string
}
export interface FinanceSettings {
  store: { enabled: boolean }
  remittance: { enabled: boolean; accounts: BankAccount[]; min_cents: number; note: string }
  withdraw: { enabled: boolean; min_cents: number; max_cents: number; fee_permille: number; fee_min_cents: number }
  invoice: { enabled: boolean; normal: boolean; special: boolean; electronic: boolean; paper: boolean; min_cents: number; note: string }
  refund: { enabled: boolean; topup_days: number; package_days: number }
}
export interface FinanceSummary {
  pending_remittances: number
  pending_remit_cents: number
  approved_remittances: number
  remitted_cents: number
  pending_withdrawals: number
  pending_withdraw_cents: number
  processing_withdrawals: number
  failed_withdrawals: number
  /** What successful withdrawals transferred, net of fees. */
  paid_out_cents: number
  pending_invoices: number
  pending_invoice_cents: number
  issued_invoices: number
  issued_cents: number
  pending_refunds: number
  pending_refund_cents: number
  processing_refunds: number
  failed_refunds: number
  refunded_cents: number
  package_orders: number
  /** Net of what was refunded. */
  package_sales_cents: number
  package_refunded_cents: number
  /** Alipay signs with certificates: withdrawals can be paid out. */
  transfer_ready: boolean
  /** Alipay is set up: top-ups can be refunded the way they came. */
  refund_ready: boolean
}
export interface FinanceFile {
  id: number
  name: string
  size: number
  content_type: string
}
interface Owned {
  user_id: number
  username?: string
}
export interface PackageOrder extends Owned {
  id: number
  order_no: string
  package_id: number
  package_name: string
  kind: 'buy' | 'renew'
  period: Period
  months: number
  amount_cents: number
  refunded_cents: number
  user_package_id: number
  period_start: string
  period_end: string
  created_at: string
  state: 'paid' | 'partly_refunded' | 'refunded'
}
export interface Remittance extends Owned {
  id: number
  remit_no: string
  amount_cents: number
  account_name: string
  account_bank: string
  account_no: string
  payer_name: string
  payer_account: string
  transferred_at: string
  reference: string
  receipt_id?: number
  note: string
  status: DocStatus
  review_note: string
  reviewed_at?: string
  created_at: string
}
export interface Withdrawal extends Owned {
  id: number
  withdraw_no: string
  amount_cents: number
  fee_cents: number
  net_cents: number
  real_name: string
  account: string
  status: DocStatus
  attempts: number
  out_biz_no: string
  order_id: string
  fail_reason: string
  review_note: string
  reviewed_at?: string
  finished_at?: string
  created_at: string
}
export interface InvoiceTitle {
  kind: 'personal' | 'company'
  title: string
  tax_no: string
  address: string
  phone: string
  bank: string
  bank_account: string
  email: string
  recipient: string
  recipient_phone: string
  mail_address: string
}
export interface Invoice extends InvoiceTitle, Owned {
  id: number
  invoice_no: string
  type: 'normal' | 'special'
  medium: 'electronic' | 'paper'
  amount_cents: number
  status: DocStatus
  number: string
  file_id?: number
  express_company: string
  express_no: string
  review_note: string
  reviewed_at?: string
  created_at: string
}
export interface Refund extends Owned {
  id: number
  refund_no: string
  target: 'topup' | 'package'
  target_id: number
  target_no: string
  amount_cents: number
  approved_cents: number
  reason: string
  revoke_time: boolean
  status: DocStatus
  fail_reason: string
  review_note: string
  reviewed_at?: string
  finished_at?: string
  created_at: string
}
export interface FinanceQuery {
  user?: number
  status?: string
  page: number
  page_size: number
}
type FinancePage<T> = Promise<{ items: T[]; total: number }>

// 工单 — the ticket types are utils/tickets.ts's, shared with the customer
// console; these are support's: the list's filters, the counts
// (service.StaffTicketSummary), who can have a ticket (service.StaffMember),
// 快捷回复 (model.TicketTemplate), 工单设置 (service.TicketSettings) and
// 邮件服务 (service.MailView).
export interface TicketQuery {
  /** open, processing, replied, closed; pending (open and processing) or active (not closed). */
  status?: string
  category?: string
  priority?: string
  /** me, none, or a staff member's id. */
  assignee?: string
  user?: number
  search?: string
  page: number
  page_size: number
}
/** 投诉管理's list: status is one state, or open for pending and processing. */
// 应用加速 — mirrors control/internal/service.L4RuleView and friends.
export type L4State = 'active' | 'disabled' | 'suspended'
export interface L4Rule {
  id: number
  user_id: number
  package_id: number
  name: string
  protocol: 'tcp' | 'udp' | 'tcp_udp'
  listen_port: number
  origins: string[]
  balance: 'round_robin' | 'ip_hash'
  proxy_protocol: 'off' | 'v1' | 'v2'
  status: 'active' | 'disabled'
  remark: string
  created_at: string
  updated_at: string
  package_name: string
  /** Where visitors connect, on listen_port; '' when the package has no line. */
  access_host: string
  state: L4State
  /** Why a suspended rule is: plan_expired, no_feature, or the quota's. */
  reason?: string
  username?: string
}
export interface L4Summary {
  total: number
  active: number
  disabled: number
  suspended: number
}
export interface L4Settings {
  port_min: number
  port_max: number
}
export interface L4Query {
  state?: L4State
  user?: number
  search?: string
  page: number
  page_size: number
}

export interface ComplaintQuery {
  status?: string
  user?: number
  search?: string
  page: number
  page_size: number
}
export interface StaffTicketSummary {
  open: number
  processing: number
  replied: number
  closed: number
  /** Not closed and the viewer's. */
  mine: number
  /** Not closed and nobody's. */
  unassigned: number
  rated: number
  /** The average rating, 0 when none. */
  rating: number
  /** 投诉 still waiting on support, for the menu's count. */
  complaints_open: number
}
export interface StaffMember {
  id: number
  username: string
  role: string
}
export interface TicketTemplate {
  id: number
  title: string
  body: string
  created_at: string
  updated_at: string
}
export interface TicketCategory {
  /** Made on save; stays as the name changes. Empty for a new one. */
  id: string
  name: string
  enabled: boolean
}
export interface TicketSettings {
  enabled: boolean
  notice: string
  categories: TicketCategory[]
  /** 0 never. */
  auto_close_days: number
  notify_customer: boolean
  notify_staff: boolean
  staff_emails: string[]
  /** What customers call support in each console; empty is 客服 / Support. */
  support_name: string
  support_name_en: string
}
export type MailSecurity = 'ssl' | 'starttls' | 'none'
export interface MailView {
  enabled: boolean
  host: string
  port: number
  security: MailSecurity
  username: string
  /** The password itself never comes back. */
  has_password: boolean
  from: string
  from_name: string
  customer_url: string
  admin_url: string
  /** The consoles' addresses when 主控域名 names their domains. */
  suggested_customer_url: string
  suggested_admin_url: string
  /** A key exists to seal the password with. */
  encryption: boolean
  /** Mail can be sent: on, a server and a sender. */
  ready: boolean
}
export interface MailInput {
  enabled: boolean
  host: string
  port: number
  security: MailSecurity
  username: string
  /** Empty keeps the stored one. */
  password: string
  from: string
  from_name: string
  customer_url: string
  admin_url: string
}

// 短信服务 — mirrors control/internal/service.SMSView and SMSInput.
/** 短信宝: the account, the signature, the messages ({code}, {minutes}). */
interface SmsbaoCommon {
  username: string
  /** The secret is the ApiKey, not the password. */
  api_key: boolean
  sign_name: string
  template: string
  intl_template: string
}
export interface SMSView {
  enabled: boolean
  /** The 国内渠道's provider (aliyun, smsbao); the 国际渠道's, '' when off. */
  provider: string
  intl_provider: string
  access_key_id: string
  has_secret: boolean
  sign_name: string
  template_code: string
  template_param: string
  intl_sign_name: string
  intl_template_code: string
  smsbao: SmsbaoCommon & { has_secret: boolean }
  ttl_minutes: number
  interval_seconds: number
  daily_per_phone: number
  encryption: boolean
  /** Mainland numbers, and the others, can be texted now. */
  ready: boolean
  intl_ready: boolean
}
/** 短信服务's form: the channels and accounts; the templates are 消息模板's. */
export interface SMSInput {
  enabled: boolean
  provider: string
  intl_provider: string
  access_key_id: string
  /** Empty keeps the stored one. */
  access_key_secret: string
  sign_name: string
  intl_sign_name: string
  /** secret: the password or ApiKey; empty keeps the stored one. */
  smsbao: Omit<SmsbaoCommon, 'template' | 'intl_template'> & { secret: string }
  ttl_minutes: number
  interval_seconds: number
  daily_per_phone: number
}
/** 消息模板's 短信 通用模板: what every scene falls back to. An empty 短信宝 message is the default one. */
export interface SMSDefaultTemplate {
  template_code: string
  template_param: string
  intl_template_code: string
  smsbao_template: string
  smsbao_intl_template: string
}
// 消息模板: each mail's words ({name} a value the event fills in) and
// each text's template.
export interface MailTemplate {
  enabled: boolean
  subject: string
  title: string
  /** One paragraph a line. */
  body: string
  button: string
}
export interface SMSTemplate {
  /** '' is 短信服务's. */
  aliyun_template: string
  aliyun_intl_template: string
  smsbao_template: string
  smsbao_intl_template: string
}
// An event's 站内信, in each language a console speaks.
export interface InboxTemplate {
  enabled: boolean
  title_zh: string
  body_zh: string
  title_en: string
  body_en: string
}
export interface MessageTemplatesView {
  mail: Array<{ event: string; recipient: 'staff' | 'customer' | 'ops' | 'visitor'; vars: string[]; template: MailTemplate; default: MailTemplate; custom: boolean }>
  sms: Array<{ scene: string; template: SMSTemplate }>
  inbox: Array<{ event: string; recipient: 'staff' | 'customer' | 'ops'; vars: string[]; template: InboxTemplate; default: InboxTemplate; custom: boolean }>
  /** Sample values for the 站内信 preview, by language (zh, en). */
  inbox_samples: Record<'zh' | 'en', Record<string, string>>
}
// An account's identity numbers in full, opened by an admin.
export interface IdentityReveal {
  user_id: number
  username: string
  real_name: string
  id_card_number?: string
  social_credit_code?: string
}

// A customer's 账户资料 as support reads it.
export interface UserProfile {
  id: number
  username: string
  nickname: string
  avatar_url: string
  qq: string
  wechat: string
  backup_name: string
  backup_phone: string
  company: string
  industry: string
  team_size: string
  site_usage: string
  province: string
  city: string
  address: string
  postcode: string
  /** ISO 3166-1 alpha-2; '' not said (mainland China). */
  country: string
  updated_at: string
}

/** What each thing gives in 经验 (会员等级); 0 gives none, a task worth none is not offered. */
export interface ExpRules {
  daily: number
  realname: number
  phone: number
  guard: number
  site: number
}

/**
 * 会员等级: 工单优先, the 成长值 VIP1 to VIP10 start at, 经验, the VIP
 * badge's look, and the curve (LV → the 经验 it starts at).
 */
export interface MemberSettings {
  vip_ticket_first: boolean
  vip_thresholds: number[]
  exp: ExpRules
  level_badge: string
  curve: { lv: number; exp: number }[]
}

/** A customer's 经验 record; actor names whoever on the platform made the change. */
export interface ExpRecord {
  id: number
  source: 'daily' | 'task' | 'adjust'
  ref: string
  amount: number
  note?: string
  created_at: string
  actor?: string
}

/** A customer's 成长值 record: the change, the growth and VIP 等级 after it, why, who. */
export interface GrowthRecord {
  id: number
  amount: number
  growth_after: number
  level_after: number
  note: string
  created_at: string
  actor?: string
}

/**
 * A customer's 会员等级 on the platform's side: the level and what every
 * level starts at (LV1 first), the VIP 等级, its 成长值 and the thresholds,
 * and both records, newest first.
 */
export interface MemberAdminView {
  user_id: number
  username: string
  level: ExpLevel
  level_starts: number[]
  vip_level: number
  vip_growth: number
  vip_thresholds: number[]
  exp_records: ExpRecord[]
  growth_records: GrowthRecord[]
}

export type MemberSettingsInput = Omit<MemberSettings, 'curve'>

// 实名认证: online verification by the methods on the 实名认证列表, each
// a provider's — 支付宝身份验证, 腾讯云人脸核身 or 阿里云号码百科.
export type RealnameProvider = 'alipay' | 'tencent' | 'aliyun' | 'sumsub' | 'veriff'
/**
 * A row of the 实名认证列表: a method — Alipay's SMART_FACE, FACE,
 * CERT_PHOTO, CERT_PHOTO_FACE; Tencent's TC_FACE, TC_ID2, TC_PHONE3;
 * Aliyun's ALI_ID2, ALI_PHONE3 — shown to customers or held back.
 */
export interface RealnameEntry {
  method: string
  enabled: boolean
}
/** 实名认证's limits and fee, shared by its page's form and view. */
interface RealnameCommon {
  enabled: boolean
  /** Each region's default — mainland China's, the international one: one of its rows shown; empty when none is. */
  default: string
  default_intl: string
  daily_attempts: number
  max_accounts: number
  /** Attempts an account has free, ever; then each costs fee_cents from its balance. */
  free_attempts: number
  fee_cents: number
}
/** 实名认证's form: each provider's own account; empty secrets keep the stored ones. */
export interface RealnameSettingsInput extends RealnameCommon {
  /** The list, in the order customers see it. */
  list: RealnameEntry[]
  alipay: { app_id: string; sign_mode: SignMode; sandbox: boolean; private_key: string; alipay_public_key: string; app_cert: string; alipay_cert: string; alipay_root_cert: string }
  tencent: { secret_id: string; secret_key: string; rule_id: string; region: string }
  aliyun: { access_key_id: string; access_key_secret: string; auth_code_id2: string; auth_code_phone3: string }
  sumsub: { app_token: string; secret_key: string; level_name: string }
  veriff: { api_key: string; shared_secret: string }
}
export interface RealnameAdminView extends RealnameCommon {
  /** Each row with whose method it is, and whether customers can use it now. */
  list: Array<RealnameEntry & { provider: RealnameProvider; region: 'cn' | 'intl'; ready: boolean }>
  alipay: { app_id: string; sign_mode: SignMode; sandbox: boolean; has_private_key: boolean; alipay_public_key: string; app_cert: string; alipay_cert: string; alipay_root_cert: string }
  tencent: { secret_id: string; has_secret_key: boolean; rule_id: string; region: string }
  aliyun: { access_key_id: string; has_access_key_secret: boolean; auth_code_id2: string; auth_code_phone3: string }
  sumsub: { app_token: string; has_secret_key: boolean; level_name: string }
  veriff: { api_key: string; has_shared_secret: boolean }
  /** The customer console's address a provider's page sends people back to; empty when unknown. */
  base_url: string
  encryption: boolean
  alipay_ready: boolean
  tencent_ready: boolean
  aliyun_ready: boolean
  sumsub_ready: boolean
  veriff_ready: boolean
  /** Mainland China's customers, and the others, can verify now. */
  ready: boolean
  intl_ready: boolean
}
/** What asking a provider with the stored account found. */
export interface RealnameTest {
  provider: RealnameProvider
  result: 'ok' | 'notReady' | 'invalidSignature' | 'invalidAppId' | 'answerSignature' | 'invalidKey' | 'notSigned' | 'authCodeMissing' | 'unreachable' | 'other'
  detail?: string
  endpoint?: string
}
export type RealnameState = 'init' | 'pending' | 'passed' | 'failed' | 'expired'
export interface RealnameRecord {
  id: number
  user_id: number
  username?: string
  outer_order_no: string
  provider: RealnameProvider
  certify_id?: string
  /** What it cost, in 分: 0 when free or refunded. */
  fee_cents: number
  method: string
  cert_name: string
  cert_no_masked: string
  /** The document: '' a 居民身份证, else PASSPORT, ID_CARD, …; and who issued it (alpha-2 or alpha-3). */
  cert_type: string
  cert_country: string
  status: RealnameState
  /** status as a person reads it: a pending one past its half hour reads expired. */
  state: RealnameState
  fail_reason?: string
  ip: string
  created_at: string
  updated_at: string
  passed_at?: string
}

// ---- 系统更新 ------------------------------------------------------------------------

/** One component of a release: the version it last changed in, and its artifact. */
export interface ReleaseComponent {
  version: string
  file: string
  sha256: string
  size: number
}
export type ReleaseComponentName = 'control' | 'agent' | 'edge'
/** The latest release on the release site, verified by the control plane. */
export interface ReleaseManifest {
  version: string
  released_at: string
  notes?: string
  components: Record<ReleaseComponentName, ReleaseComponent>
}
export interface ReleaseEntry {
  version: string
  released_at: string
  notes?: string
  changed: ReleaseComponentName[]
}
/** cdnx-updater's account of a run: running, then succeeded, failed (nothing changed) or rolled_back. */
export interface UpdateStatus {
  id: string
  version: string
  from: string
  state: 'running' | 'succeeded' | 'failed' | 'rolled_back'
  phase: 'download' | 'backup' | 'swap' | 'build' | 'restart' | 'migrate' | 'check' | 'rollback' | 'done'
  message?: string
  backup?: string
  log?: string[]
  started_at: string
  finished_at?: string
}
/** One finished update, from the system task queue. */
export interface SystemUpdateRun {
  id: number
  target?: string
  status: 'success' | 'failed'
  error_msg?: string
  result_json?: { code?: string; message?: string; params?: Record<string, unknown>; detail?: { backup?: string; phase?: string } }
  created_at: string
  finished_at?: string
}
export interface SystemUpdateView {
  current: string
  /** This build carries the release public key: it can verify updates. */
  release_key: boolean
  settings: { auto_check: boolean }
  /** Where releases come from: the licence's repository. */
  repository?: string
  /** Why there is none: license.noRepository (activate), license.repoTokenExpired (not renewed). */
  no_repository?: { code: string; detail?: string }
  checked_at?: string
  check_error?: { code: 'noRelease' | 'badSignature' | 'badManifest' | 'unauthorized' | 'unreachable'; detail?: string }
  latest?: ReleaseManifest
  history: ReleaseEntry[]
  available: boolean
  updater?: { version: string; installed_at: string }
  status?: UpdateStatus
  /** A request cdnx-updater has not taken yet. */
  pending: boolean
  nodes: { total: number; outdated: number }
  /** The latest release's agent and edge, kept here for the nodes. */
  node_artifacts?: { ready: boolean; preparing: boolean; error?: { code: string; detail?: string } }
  runs: SystemUpdateRun[]
}
/** The newest upgrade task of a node and component. */
export interface NodeUpgrade {
  id: number
  node_id: number
  component: 'agent' | 'edge'
  version: string
  status: 'pending' | 'leased' | 'success' | 'failed'
  error_msg?: string
  /** A failure the page can say what to do about: the node's one-time bootstrap is missing. */
  reason?: 'agentTooOld' | 'noUpdater' | 'noAnswer'
  created_at: string
  finished_at?: string
}

export type SMSPurpose = 'bind_phone' | 'login' | 'reset_password' | 'test'
// One send: the code's state as a person reads it, never the code.
export interface SMSRecord {
  id: number
  user_id: number
  username?: string
  phone: string
  purpose: SMSPurpose
  attempts: number
  status: 'pending' | 'sent' | 'failed'
  state: 'pending' | 'failed' | 'used' | 'expired' | 'valid'
  error?: string
  biz_id?: string
  ip: string
  expires_at: string
  used_at?: string
  created_at: string
}

// What /auth/login and /auth/login/mfa answer: a token, or — the password
// of an account with an MFA device — a ticket for the second step.
export interface LoginResult {
  access_token?: string
  expires_in?: number
  user?: any
  mfa_required?: boolean
  mfa_ticket?: string
  /** The ways the second step may be finished: "mfa", "sms". */
  methods?: SecondStep[]
  /** Where a texted code goes, masked: 138****0000. */
  phone?: string
}
export type SecondStep = 'mfa' | 'sms'
// A code texted: where to (masked), how long it lasts, how long until
// another may be asked for — all in seconds.
export interface SentCode {
  phone: string
  expires_in: number
  interval: number
}
// A new token for this session (修改密码, 退出其他设备).
export interface SessionToken {
  access_token: string
  expires_in: number
}
// One sign-in, or one wrong password or code given (登录记录).
export interface LoginRecord {
  id: number
  ip: string
  user_agent: string
  result: 'success' | 'bad_password' | 'bad_mfa' | 'bad_sms' | 'inactive' | 'password_reset'
  /** How: password, code, passkey or sub_user; '' on records from before it was kept. */
  method?: string
  /** The 子用户 who signed in, when one did. */
  sub_user?: string
  created_at: string
}
// A new MFA device's secret and the otpauth address its QR code carries.
export interface MFASetup {
  secret: string
  uri: string
}

// AI 助手 — mirrors control/internal/service AssistantView, AssistantInput,
// AssistantTestResult and AssistantUsage.
export type AssistantProvider = 'deepseek' | 'qwen' | 'ark' | 'openai' | 'custom'
export type AssistantIcon = 'spark' | 'pinwheel' | 'cloud' | 'bubble' | 'cube' | 'orb'
export type AssistantLauncher = 'circle' | 'capsule'
export type AssistantPlacement = 'float' | 'navbar' | 'both'
export interface AssistantView {
  enabled: boolean
  name: string
  name_en: string
  icon: AssistantIcon
  animated: boolean
  /** Where the customer console offers it: the bottom right, the navbar, or both. */
  placement: AssistantPlacement
  /** The navbar entry carries the name beside the mark. */
  nav_label: boolean
  /** The floating button's shape. */
  launcher: AssistantLauncher
  /** The input box's 跑马灯: a light round the border; the suggestions turning over inside. */
  input_glow: boolean
  input_hints: boolean
  provider: AssistantProvider
  base_url: string
  model: string
  has_key: boolean
  temperature: number
  max_tokens: number
  account_context: boolean
  /** 0 is no limit. */
  daily_limit: number
  prompt: string
  /** Suggested questions, for a Chinese console and an English one. */
  suggestions: string[]
  suggestions_en: string[]
  encryption: boolean
  ready: boolean
}
export interface AssistantInput extends Omit<AssistantView, 'has_key' | 'encryption' | 'ready'> {
  /** Empty keeps the stored key (unless the address changed). */
  api_key: string
}
export interface AssistantTestResult {
  model: string
  reply: string
  latency_ms: number
}
export interface AssistantDay {
  day: string
  questions: number
  users: number
  prompt_tokens: number
  completion_tokens: number
  failed: number
}
export interface AssistantUsage {
  today: AssistantDay
  days: AssistantDay[]
}

// 站内信: what the platform told this account (消息中心), and how it wants
// each event (消息接收配置). Both consoles.
export interface InboxItem {
  id: number
  category: string
  event: string
  title: string
  body: string
  /** A page of this console, '' for none. */
  link: string
  read_at: string | null
  created_at: string
}
export interface InboxPage {
  items: InboxItem[]
  total: number
  unread: number
}
export interface NotifyPrefRow {
  event: string
  category: string
  inbox: boolean
  mail: boolean
  /** False for support's events: their mail goes where 工单设置 sends it. */
  mail_available: boolean
}

// 配置生效: what a node's edge serves, judged against what was published
// (control/internal/service/serving.go).
export type ServingStatus = 'ok' | 'syncing' | 'partial' | 'risk' | 'probe_failed' | 'not_effective' | 'unknown'
export interface EdgeIssue {
  subject: string
  reason: string
}
export interface EdgeState {
  pid: number
  version: string
  started_at: number
  written_at: number
  bundle_root: string
  node_config: string
  node_config_present: boolean
  in_unit: boolean | null
  bundle: {
    version: string
    loaded_at: number
    expected: number
    loaded: number
    skipped_total: number
    skipped: EdgeIssue[] | null
    warnings_total: number
    warnings: EdgeIssue[] | null
    parts: EdgeIssue[] | null
  }
  failure: { version: string; error: string; at: number } | null
}
export interface ProbeFailure {
  host: string
  code: string
  detail?: string
  for_sec: number
}
export interface ProbeResult {
  version: string
  total: number
  probed: number
  failed_total: number
  failed: ProbeFailure[] | null
}
export interface ServingSite {
  host: string
  code: string
  reason?: string
  for_sec?: number
}
export interface ServingDetail {
  reason?: string
  expected?: string
  serving?: string
  detail?: string
  sites?: ServingSite[]
  sites_total?: number
}
export interface ServingAlert {
  id: number
  node_id: number
  kind: ServingStatus
  opened_at: string
  resolved_at?: string
  detail: ServingDetail
}
export interface NodeServing {
  /** The version published, which the node should serve. */
  latest?: string
  status: ServingStatus
  reason?: string
  kinds?: Partial<Record<ServingStatus, ServingDetail>>
  edge?: EdgeState
  edge_age_sec: number
  edge_error?: string
  probe?: ProbeResult
  probe_age_sec: number
  applied?: { version: string; effective: string; reason?: string; detail?: string }
  received_at?: string
  alerts: ServingAlert[]
}
/** How many of the nodes a site should be served by serve it. */
export interface SiteServing {
  serving: number
  nodes: number
  unknown: number
}

/** An extra hostname of a site (model.DomainAlias). The nodes serve it
 *  only once active: its CNAME verified to pass through the site's own
 *  record ('cname'), forced by an admin ('admin'), or carried over from
 *  before aliases were verified ('legacy'). */
export interface DomainAlias {
  id: number
  domain_id: number
  hostname: string
  status: 'pending' | 'active'
  verified_via?: 'cname' | 'admin' | 'legacy' | ''
  verified_at?: string
  created_at: string
}

/** One look at a pending alias's CNAME (service.AliasVerifyResult). */
export interface AliasVerifyResult {
  alias: DomainAlias
  expected: string
  resolved?: string
  matched: boolean
  reason?: { code?: string; message?: string; params?: Record<string, unknown> }
}

/** What keeps a site from being served properly (service.SiteReadiness):
 *  no origin, so every request is a 502; hostnames with no certificate,
 *  whose HTTPS handshake fails; hostnames served one that does not cover
 *  them; aliases still pending CNAME verification. Empty fields are left
 *  out. */
export interface SiteReadiness {
  no_origin?: boolean
  no_cert?: string[]
  cert_mismatch?: string[]
  alias_pending?: string[]
}

// Mirrors control/internal/service.CCAutoStatus: a site's 自适应 CC
// across the nodes.
export interface CCAutoStatus {
  active: boolean
  nodes: number
  since?: string
  reason?: 'failing' | 'slow'
  rps: number
  baseline_rps: number
  failed_ratio: number
  ttfb_ms: number
}

export const api = {
  // Worded in the console's language: 站内信 are kept as their parts and
  // rendered by 消息模板's 站内信 templates for the reader.
  notifications(params: { unread?: boolean; category?: string; page?: number; page_size?: number }): Promise<InboxPage> {
    const { unread, ...rest } = params
    return client
      .get('/notifications', { params: { ...rest, unread: unread ? 1 : undefined, lang: i18n.global.locale.value } })
      .then((r) => r.data)
  },
  /** No ids: every unread one. */
  readNotifications(ids: number[]): Promise<{ unread: number }> {
    return client.post('/notifications/read', { ids }).then((r) => r.data)
  },
  deleteNotifications(ids: number[]): Promise<{ unread: number }> {
    return client.post('/notifications/delete', { ids }).then((r) => r.data)
  },
  notifyPrefs(): Promise<{ items: NotifyPrefRow[] }> {
    return client.get('/notifications/prefs').then((r) => r.data)
  },
  setNotifyPref(event: string, pref: { inbox: boolean; mail: boolean }): Promise<{ items: NotifyPrefRow[] }> {
    return client.put(`/notifications/prefs/${event}`, pref).then((r) => r.data)
  },
  // Auth. /auth/* lives at the root (not under /api/v1) because it's
  // pre-version infrastructure shared by all API versions, so we bypass
  // the axios instance's baseURL by passing an absolute path explicitly.
  login(username: string, password: string, captcha?: CaptchaAnswer) {
    return client.post('/auth/login', { username, password, captcha }, { baseURL: '' })
      .then((r) => r.data as LoginResult)
  },
  me() {
    return client.get('/me').then((r) => r.data)
  },
  // The second step for an account with an MFA device: the ticket the
  // password got, and the device's code.
  loginMFA(ticket: string, code: string, method: SecondStep = 'mfa') {
    return client.post('/auth/login/mfa', { ticket, code, method }, { baseURL: '' })
      .then((r) => r.data as LoginResult)
  },
  // 登录保护 by text: the code for the account the ticket was handed to.
  sendLoginCode(ticket: string) {
    return client.post('/auth/login/sms', { ticket }, { baseURL: '' }).then((r) => r.data as SentCode)
  },
  // 找回密码: a code to the account's proved phone, then the new password.
  sendResetCode(username: string, captcha?: CaptchaAnswer) {
    return client.post('/auth/password/code', { username, captcha }, { baseURL: '' }).then((r) => r.data as SentCode)
  },
  resetPassword(username: string, code: string, newPassword: string) {
    return client.post('/auth/password/reset', { username, code, new_password: newPassword }, { baseURL: '' })
  },

  // 安全中心: the signed-in account's own. Changing the password and ending
  // the other sessions hand this session a new token (useAuthStore().useToken).
  accountLogins(limit = 50) {
    return client.get('/account/logins', { params: { limit } }).then((r) => (r.data.items ?? []) as LoginRecord[])
  },
  changePassword(current: string, next: string) {
    return client.put('/account/password', { current_password: current, new_password: next })
      .then((r) => r.data as SessionToken)
  },
  updateContact(body: { password: string; email: string; phone: string }) {
    return client.put('/account/contact', body).then((r) => r.data)
  },
  newMFASecret() {
    return client.post('/account/mfa/secret').then((r) => r.data as MFASetup)
  },
  enableMFA(body: { password: string; secret: string; code: string }) {
    return client.post('/account/mfa', body).then((r) => r.data)
  },
  disableMFA(code: string) {
    return client.post('/account/mfa/disable', { code }).then((r) => r.data)
  },
  endOtherSessions() {
    return client.post('/account/sessions/end-others').then((r) => r.data as SessionToken)
  },
  // Proving a phone with a texted code; an empty phone removes it.
  sendPhoneCode(phone: string, captcha?: CaptchaAnswer) {
    return client.post('/account/phone/code', { phone, captcha }).then((r) => r.data as SentCode)
  },
  changePhone(body: { password: string; phone: string; code: string }) {
    return client.put('/account/phone', body).then((r) => r.data)
  },
  setLoginSMS(password: string, enabled: boolean) {
    return client.put('/account/login-sms', { password, enabled }).then((r) => r.data)
  },

  // Nodes. serving is 配置生效 by node id (absent from a control plane
  // before it).
  listNodes(status?: string): Promise<{ items: any[]; total: number; serving?: Record<string, NodeServing> }> {
    const cfg: AxiosRequestConfig = { params: status ? { status } : undefined }
    return client.get('/admin/nodes', cfg).then((r) => r.data)
  },
  nodeServing(id: number): Promise<{ serving: NodeServing; history: ServingAlert[] }> {
    return client.get(`/admin/nodes/${id}/serving`).then((r) => r.data)
  },
  domainServing(id: number): Promise<SiteServing> {
    return client.get(`/admin/domains/${id}/serving`).then((r) => r.data)
  },
  createRegisterToken(ttlSeconds = 24 * 3600) {
    return client.post('/admin/nodes/register-tokens', { ttl_seconds: ttlSeconds }).then((r) => r.data)
  },
  updateNode(id: number, body: { alias?: string; region?: string; isp?: string }) {
    return client.put(`/admin/nodes/${id}`, body).then((r) => r.data)
  },
  initNode(id: number) {
    return client.put(`/admin/nodes/${id}/init`).then((r) => r.data)
  },
  getNode(id: number) {
    return client.get(`/admin/nodes/${id}`).then((r) => r.data)
  },
  // Host metrics history. Returns 503 when ClickHouse isn't configured —
  // callers should degrade to the live values rather than error the page.
  nodeMetrics(id: number, hours = 1) {
    return client.get(`/admin/nodes/${id}/metrics`, { params: { hours } }).then((r) => r.data)
  },
  listNodeIPs(id: number) {
    return client.get(`/admin/nodes/${id}/ips`).then((r) => r.data)
  },
  addNodeIP(id: number, ip: string, note = '', inDns = true) {
    return client.post(`/admin/nodes/${id}/ips`, { ip, note, in_dns: inDns }).then((r) => r.data)
  },
  setNodeIPInDNS(id: number, ipId: number, inDns: boolean) {
    return client.put(`/admin/nodes/${id}/ips/${ipId}`, { in_dns: inDns }).then((r) => r.data)
  },
  deleteNodeIP(id: number, ipId: number) {
    return client.delete(`/admin/nodes/${id}/ips/${ipId}`).then((r) => r.data)
  },

  // ---- logs & analytics (fleet-wide; the customer console has its own
  // /stats/* endpoints scoped to the caller's domains) ----
  searchAccessLogs(params: Record<string, unknown>) {
    return client.get('/admin/logs/access', { params }).then((r) => r.data)
  },
  // 仪表盘: counts, new per day and what needs attention. tz_offset is
  // the browser's minutes east of UTC, so the days are the operator's.
  adminDashboard(params: { tz_offset: number }) {
    return client.get('/admin/dashboard', { params }).then((r) => r.data)
  },
  // 监控趋势: traffic and bans over a window (1h, today, yesterday, 7d,
  // 30d), with the window it is compared with. tz_offset sets the days.
  adminDashboardMonitor(params: { range: string; tz_offset: number }) {
    return client.get('/admin/dashboard/monitor', { params }).then((r) => r.data)
  },
  // TOP10 数据: the ten busiest sites, URLs, client IPs or countries over the
  // last hour, today or yesterday (the operator's days, tz_offset).
  adminDashboardTop(params: { dim: string; range: string; tz_offset: number }) {
    return client.get('/admin/dashboard/top', { params }).then((r) => r.data)
  },
  // 数据分析: the traffic of the platform, a customer, a sold package or a
  // site (the server counts a site's aliases in), over a window and the
  // window before it; split by dim; ranked by dim. See AnalyticsQuery.
  adminAnalyticsOverview(params: AnalyticsQuery) {
    return client.get('/admin/analytics/overview', { params }).then((r) => r.data)
  },
  adminAnalyticsBreakdown(params: AnalyticsQuery & { dim: string }) {
    return client.get('/admin/analytics/breakdown', { params }).then((r) => r.data)
  },
  adminAnalyticsRank(params: AnalyticsQuery & { dim: string }) {
    return client.get('/admin/analytics/rank', { params }).then((r) => r.data)
  },
  // 回源统计: the requests that went to the origin, over the same scopes and
  // windows.
  adminOriginOverview(params: AnalyticsQuery): Promise<OOverview> {
    return client.get('/admin/analytics/origin/overview', { params }).then((r) => r.data)
  },
  adminOriginBreakdown(params: AnalyticsQuery & { dim: string }): Promise<ABreakdown> {
    return client.get('/admin/analytics/origin/breakdown', { params }).then((r) => r.data)
  },
  adminOriginRank(params: AnalyticsQuery & { dim: string }): Promise<ORank> {
    return client.get('/admin/analytics/origin/rank', { params }).then((r) => r.data)
  },
  // 访问统计 and 访问日志: the access log under conditions (filters, see
  // utils/logdims) — counted, its most frequent values offered, ranked by
  // any dimension with what tells abuse apart, and its rows.
  adminAccessOverview(params: AnalyticsQuery): Promise<XOverview> {
    return client.get('/admin/analytics/access/overview', { params }).then((r) => r.data)
  },
  adminAccessTop(params: AnalyticsQuery & { dim: string; metric?: string; n?: number }): Promise<ABreakdown> {
    return client.get('/admin/analytics/access/top', { params }).then((r) => r.data)
  },
  adminAccessRank(params: AnalyticsQuery & { dim: string; sort: string; n: number }): Promise<XRank> {
    return client.get('/admin/analytics/access/rank', { params }).then((r) => r.data)
  },
  adminAccessLogs(params: AnalyticsQuery & { limit: number; offset: number }): Promise<LogPage> {
    return client.get('/admin/analytics/access/logs', { params }).then((r) => r.data)
  },
  // 安全事件, under 数据分析's filters (or the older hours= and domain=):
  // the events, what was turned away and from where, the addresses and the
  // hostnames turned away most.
  searchSecurityEvents(params: Record<string, unknown>) {
    return client.get('/admin/logs/security', { params }).then((r) => r.data)
  },
  securitySummary(params: Record<string, unknown>) {
    return client.get('/admin/logs/security/summary', { params }).then((r) => r.data)
  },
  topAttackers(params: Record<string, unknown> = {}) {
    return client.get('/admin/logs/top-attackers', { params }).then((r) => r.data)
  },
  securityTopHosts(params: Record<string, unknown> = {}) {
    return client.get('/admin/logs/security/top-hosts', { params }).then((r) => r.data)
  },

  // Audit logs.
  listAuditLogs(params: {
    actor_type?: string
    actor_id?: string
    resource_type?: string
    action?: string
    from?: string
    to?: string
    limit?: number
  } = {}) {
    return client.get('/admin/audit-logs', { params }).then((r) => r.data)
  },

  // Packages + user-package assignments.
  listPackages() {
    return client.get('/admin/packages').then((r) => r.data)
  },
  createPackage(body: Record<string, unknown>) {
    return client.post('/admin/packages', body).then((r) => r.data)
  },
  updatePackage(id: number, body: Record<string, unknown>) {
    return client.put(`/admin/packages/${id}`, body).then((r) => r.data)
  },
  deletePackage(id: number) {
    return client.delete(`/admin/packages/${id}`).then((r) => r.data)
  },
  // Sold packages ("已售套餐"): every customer's subscriptions.
  listSubscriptions(): Promise<{ items: Subscription[]; total: number }> {
    return client.get('/admin/subscriptions').then((r) => r.data)
  },
  assignSubscription(body: {
    user_id: number
    package_id: number
    expires_at?: string
    duration_days?: number
    auto_renew?: boolean
  }) {
    return client.post('/admin/subscriptions', body).then((r) => r.data)
  },
  updateSubscription(id: number, body: { expires_at: string; auto_renew: boolean }) {
    return client.put(`/admin/subscriptions/${id}`, body).then((r) => r.data)
  },
  renewSubscription(id: number, days: number) {
    return client.post(`/admin/subscriptions/${id}/renew`, { days }).then((r) => r.data)
  },
  cancelSubscription(id: number) {
    return client.post(`/admin/subscriptions/${id}/cancel`).then((r) => r.data)
  },
topUpSubscription(id: number, body: { traffic_gb: number; requests_million: number }) {
    return client.post(`/admin/subscriptions/${id}/quota/top-up`, body).then((r) => r.data)
  },
  resetSubscriptionUsage(id: number) {
    return client.post(`/admin/subscriptions/${id}/quota/reset`).then((r) => r.data)
  },
    resumeSubscription(id: number) {
    return client.post(`/admin/subscriptions/${id}/resume`).then((r) => r.data)
  },
  listUserPackages(userId: number) {
    return client.get(`/admin/users/${userId}/packages`).then((r) => r.data)
  },

  // Node groups ("线路组") — DNS sync targets.
  listNodeGroups() {
    return client.get('/admin/node-groups').then((r) => r.data)
  },
  createNodeGroup(body: { name: string; description?: string; resolve_value: string; cname_zone_id: number; line?: string }) {
    return client.post('/admin/node-groups', body).then((r) => r.data)
  },
  updateNodeGroup(id: number, body: { name: string; description?: string; resolve_value: string; cname_zone_id: number; line?: string }) {
    return client.put(`/admin/node-groups/${id}`, body).then((r) => r.data)
  },
  deleteNodeGroup(id: number) {
    return client.delete(`/admin/node-groups/${id}`).then((r) => r.data)
  },
  // Membership is edited group-side: one PUT carrying the whole set,
  // rather than a request per node that could half-apply.
  listNodeGroupMembers(groupId: number) {
    return client.get(`/admin/node-groups/${groupId}/members`).then((r) => r.data)
  },
  setNodeGroupMembers(groupId: number, nodeIds: number[]) {
    return client.put(`/admin/node-groups/${groupId}/members`, { node_ids: nodeIds }).then((r) => r.data)
  },

  // DNS providers — platform-scoped shared credentials.
  listDNSProviders() {
    return client.get('/admin/dns-providers').then((r) => r.data)
  },
  createDNSProvider(body: { type: string; name: string; access_key: string; access_secret: string }) {
    return client.post('/admin/dns-providers', body).then((r) => r.data)
  },
  updateDNSProvider(id: number, body: { type: string; name: string; access_key?: string; access_secret?: string }) {
    return client.put(`/admin/dns-providers/${id}`, body).then((r) => r.data)
  },
  deleteDNSProvider(id: number) {
    return client.delete(`/admin/dns-providers/${id}`).then((r) => r.data)
  },
  testDNSProvider(id: number) {
    return client.post(`/admin/dns-providers/${id}/test`).then((r) => r.data)
  },

  // Users (customer account management — see service.Users: staff
  // accounts are never reachable through this surface).
  /** vip: 1 keeps only the VIP customers. levels: id → 等级. */
  listUsers(
    params: { status?: string; group_id?: number; search?: string; vip?: 1 } = {},
  ): Promise<{ items: any[]; total: number; balances: Record<string, number>; levels: Record<string, number> }> {
    return client.get('/admin/users', { params }).then((r) => r.data)
  },
  getUser(id: number) {
    return client.get(`/admin/users/${id}`).then((r) => r.data)
  },
  createUser(body: Record<string, unknown>) {
    return client.post('/admin/users', body).then((r) => r.data)
  },
  updateUser(id: number, body: Record<string, unknown>) {
    return client.put(`/admin/users/${id}`, body).then((r) => r.data)
  },
  // A customer's 安全中心, from the platform's side.
  /** An account's sealed identity numbers in full; admins only, audited. */
  revealIdentity(id: number): Promise<IdentityReveal> {
    return client.post(`/admin/users/${id}/identity/reveal`).then((r) => r.data)
  },
  /** The number a 实名认证 was made with; admins only, audited. */
  revealRealnameRecord(id: number): Promise<{ id: number; user_id: number; cert_name: string; cert_no: string }> {
    return client.post(`/admin/realname/records/${id}/reveal`).then((r) => r.data)
  },
  /** A customer's 账户资料, read only. */
  userProfile(id: number): Promise<UserProfile> {
    return client.get(`/admin/users/${id}/profile`).then((r) => r.data)
  },
  userLogins(id: number, limit = 50) {
    return client.get(`/admin/users/${id}/logins`, { params: { limit } }).then((r) => (r.data.items ?? []) as LoginRecord[])
  },
  resetUserLoginSMS(id: number) {
    return client.delete(`/admin/users/${id}/login-sms`).then((r) => r.data)
  },
  resetUserMFA(id: number) {
    return client.delete(`/admin/users/${id}/mfa`).then((r) => r.data)
  },
  endUserSessions(id: number) {
    return client.post(`/admin/users/${id}/sessions/end`)
  },
  deleteUser(id: number) {
    return client.delete(`/admin/users/${id}`).then((r) => r.data)
  },

  // User groups ("用户分组", customer tiering — distinct from line groups).
  // Package groups ("套餐分组") — a label for filing packages. Distinct from
  // user groups, which group accounts.
  listPackageGroups() {
    return client.get('/admin/package-groups').then((r) => r.data)
  },
  createPackageGroup(body: { name: string; description?: string }) {
    return client.post('/admin/package-groups', body).then((r) => r.data)
  },
  updatePackageGroup(id: number, body: { name: string; description?: string }) {
    return client.put(`/admin/package-groups/${id}`, body).then((r) => r.data)
  },
  deletePackageGroup(id: number) {
    return client.delete(`/admin/package-groups/${id}`).then((r) => r.data)
  },
  listUserGroups() {
    return client.get('/admin/user-groups').then((r) => r.data)
  },
  createUserGroup(body: { name: string; description?: string }) {
    return client.post('/admin/user-groups', body).then((r) => r.data)
  },
  updateUserGroup(id: number, body: { name: string; description?: string }) {
    return client.put(`/admin/user-groups/${id}`, body).then((r) => r.data)
  },
  deleteUserGroup(id: number) {
    return client.delete(`/admin/user-groups/${id}`).then((r) => r.data)
  },

  // Regions ("地区") — a naming registry for nodes. Distinct from line
  // groups, which are what actually steer DNS.
  listRegions() {
    return client.get('/admin/regions').then((r) => r.data)
  },
  createRegion(body: { code: string; name: string; remark?: string }) {
    return client.post('/admin/regions', body).then((r) => r.data)
  },
  // migrate_nodes opts in to re-pointing the nodes that carry the old code.
  // Without it the server refuses a code change that would strand them, and
  // reports how many there are so the console can ask.
  updateRegion(id: number, body: { code: string; name: string; remark?: string; migrate_nodes?: boolean }) {
    return client.put(`/admin/regions/${id}`, body).then((r) => r.data)
  },
  deleteRegion(id: number) {
    return client.delete(`/admin/regions/${id}`).then((r) => r.data)
  },

  // 接入域名 — the base domains customer sites CNAME into. Distinct from
  // the per-record CNAME hostnames under a line group.
  listCnameZones() {
    return client.get('/admin/cname-zones').then((r) => r.data)
  },
  createCnameZone(body: { dns_provider_id: number; domain: string; remark?: string; status?: string; ttl?: number }) {
    return client.post('/admin/cname-zones', body).then((r) => r.data)
  },
  updateCnameZone(id: number, body: { dns_provider_id: number; domain: string; remark?: string; status?: string; ttl?: number }) {
    return client.put(`/admin/cname-zones/${id}`, body).then((r) => r.data)
  },
  // The zone's DNS plan: its name and the shortest TTL it accepts.
  cnameZonePlan(id: number) {
    return client.get(`/admin/cname-zones/${id}/plan`).then((r) => r.data)
  },
  deleteCnameZone(id: number) {
    return client.delete(`/admin/cname-zones/${id}`).then((r) => r.data)
  },

  // Domains — full admin control across every customer (see
  // internal/api/admin/domains.go: service.Domains already carries an
  // isAdmin bypass end-to-end, so these are the same operations the
  // customer console has, just unrestricted by ownership).
  // Which sites are suspended for an expired package: id -> reason.
  domainSuspensions(): Promise<{ items: Record<string, string> }> {
    return client.get('/admin/domain-suspensions').then((r) => r.data)
  },
  // What each site is missing to be served properly (sites missing
  // nothing are left out), and one site's.
  domainReadinessAll(): Promise<{ items: Record<string, SiteReadiness> }> {
    return client.get('/admin/domain-readiness').then((r) => r.data)
  },
  domainReadiness(id: number): Promise<SiteReadiness> {
    return client.get(`/admin/domains/${id}/readiness`).then((r) => r.data)
  },
  listDomains() {
    return client.get('/admin/domains').then((r) => r.data)
  },
  updateDomainChallenge(id: number, body: Record<string, unknown>) {
    return client.put(`/admin/domains/${id}/challenge`, body).then((r) => r.data)
  },
  // 自适应 CC right now: whether nodes are verifying the site's new
  // visitors, since when, and what they saw.
  ccAutoStatus(id: number): Promise<CCAutoStatus> {
    return client.get(`/admin/domains/${id}/cc-auto`).then((r) => r.data)
  },
  updateCCProtection(
    id: number,
    body: {
      level: string
      fail_ban: { enabled: boolean; max_failures: number; window_seconds: number; ban_seconds: number }
      // 自适应 CC: off | loose | normal | strict, and the verification.
      auto?: { level: string; mode: string }
    },
  ) {
    return client.put(`/admin/domains/${id}/cc-protection`, body).then((r) => r.data)
  },
  updateGeoBlock(
    id: number,
    body: {
      mode: string
      action: string
      regions: string[]
      subdivisions: string[]
      cities: { country: string; name: string }[]
      ban_seconds: number
    },
  ) {
    return client.put(`/admin/domains/${id}/geo-block`, body).then((r) => r.data)
  },
  getDomain(id: number) {
    return client.get(`/admin/domains/${id}`).then((r) => r.data)
  },
  createDomain(body: Record<string, unknown>) {
    return client.post('/admin/domains', body).then((r) => r.data)
  },
  updateDomain(id: number, body: Record<string, unknown>) {
    return client.put(`/admin/domains/${id}`, body).then((r) => r.data)
  },
  deleteDomain(id: number) {
    return client.delete(`/admin/domains/${id}`).then((r) => r.data)
  },
  publishDomain(id: number) {
    return client.post(`/admin/domains/${id}/publish`).then((r) => r.data)
  },
  disableDomain(id: number) {
    return client.post(`/admin/domains/${id}/disable`).then((r) => r.data)
  },
  enableDomain(id: number) {
    return client.post(`/admin/domains/${id}/enable`).then((r) => r.data)
  },
  listOrigins(domainId: number) {
    return client.get(`/admin/domains/${domainId}/origins`).then((r) => r.data)
  },
  addOrigin(domainId: number, body: Record<string, unknown>) {
    return client.post(`/admin/domains/${domainId}/origins`, body).then((r) => r.data)
  },
  deleteOrigin(domainId: number, originId: number) {
    return client.delete(`/admin/domains/${domainId}/origins/${originId}`).then((r) => r.data)
  },
  listDomainAliases(domainId: number) {
    return client.get(`/admin/domains/${domainId}/aliases`).then((r) => r.data)
  },
  addDomainAlias(domainId: number, hostname: string) {
    return client.post(`/admin/domains/${domainId}/aliases`, { hostname }).then((r) => r.data)
  },
  deleteDomainAlias(domainId: number, aliasId: number) {
    return client.delete(`/admin/domains/${domainId}/aliases/${aliasId}`).then((r) => r.data)
  },
  // A pending alias's CNAME, checked now: a match makes it active.
  verifyDomainAlias(domainId: number, aliasId: number): Promise<AliasVerifyResult> {
    return client.post(`/admin/domains/${domainId}/aliases/${aliasId}/verify`).then((r) => r.data)
  },
  // Makes a pending alias active without its CNAME check (强制生效).
  forceDomainAlias(domainId: number, aliasId: number): Promise<DomainAlias> {
    return client.post(`/admin/domains/${domainId}/aliases/${aliasId}/force`).then((r) => r.data)
  },
  getDomainSyncStatus(domainId: number) {
    return client.get(`/admin/domains/${domainId}/sync-status`).then((r) => r.data)
  },
  listDomainCertificates(domainId: number) {
    return client.get(`/admin/domains/${domainId}/certificates`).then((r) => r.data)
  },
  uploadCertificate(domainId: number, body: { cert_pem: string; key_pem: string }) {
    return client.post(`/admin/domains/${domainId}/certificates`, body).then((r) => r.data)
  },
  // A site's certificate table: one row per hostname, with what it serves
  // and what its latest issuance did.
  listHostCerts(domainId: number) {
    return client.get(`/admin/domains/${domainId}/host-certs`).then((r) => r.data)
  },
  // What the site's package includes and how much of its counts is used:
  // an operator may set up the rest, which then does not apply.
  siteEntitlements(domainId: number): Promise<SiteEntitlements> {
    return client.get(`/admin/domains/${domainId}/entitlements`).then((r) => r.data)
  },
  bindHostCert(domainId: number, host: string, certId: number) {
    return client
      .put(`/admin/domains/${domainId}/host-certs/${encodeURIComponent(host)}`, { cert_id: certId })
      .then((r) => r.data)
  },
  unbindHostCert(domainId: number, host: string) {
    return client.delete(`/admin/domains/${domainId}/host-certs/${encodeURIComponent(host)}`).then((r) => r.data)
  },
  // Queued in the system task queue; listHostCerts reports the outcome.
  issueHostCert(domainId: number, host: string, body: { email: string; ca?: string }) {
    return client
      .post(`/admin/domains/${domainId}/host-certs/${encodeURIComponent(host)}/acme`, body)
      .then((r) => r.data)
  },
  retryHostCert(domainId: number, host: string) {
    return client
      .post(`/admin/domains/${domainId}/host-certs/${encodeURIComponent(host)}/acme/retry`)
      .then((r) => r.data)
  },
  cancelHostCert(domainId: number, host: string) {
    return client
      .delete(`/admin/domains/${domainId}/host-certs/${encodeURIComponent(host)}/acme`)
      .then((r) => r.data)
  },
  listAllCertificates() {
    return client.get('/admin/certificates').then((r) => r.data)
  },

  // Rules — kind ∈ acl | cc | cache | waf | bot. Same service.Rules the
  // customer console uses (isAdmin bypass built in), mounted under
  // /admin so an operator can manage any customer's domain rules.
  listRules(domainId: number, kind: string) {
    return client.get(`/admin/domains/${domainId}/rules/${kind}`).then((r) => r.data)
  },
  createRule(domainId: number, kind: string, body: Record<string, unknown>) {
    return client.post(`/admin/domains/${domainId}/rules/${kind}`, body).then((r) => r.data)
  },
  updateRule(domainId: number, kind: string, ruleId: number, body: Record<string, unknown>) {
    return client.put(`/admin/domains/${domainId}/rules/${kind}/${ruleId}`, body).then((r) => r.data)
  },
  deleteRule(domainId: number, kind: string, ruleId: number) {
    return client.delete(`/admin/domains/${domainId}/rules/${kind}/${ruleId}`).then((r) => r.data)
  },
  // 支付配置, 充值订单 and a customer's balance.
  payment(): Promise<PaymentView> {
    return client.get('/admin/payment').then((r) => r.data)
  },
  saveTopUpSettings(body: TopUpSettings): Promise<PaymentView> {
    return client.put('/admin/payment/topup', body).then((r) => r.data)
  },
  saveAlipay(body: AlipayInput): Promise<PaymentView> {
    return client.put('/admin/payment/alipay', body).then((r) => r.data)
  },
  testAlipay(): Promise<AlipayTest> {
    return client.post('/admin/payment/alipay/test').then((r) => r.data)
  },
  billingSummary(): Promise<BillingSummary> {
    return client.get('/admin/billing/summary', { params: { tz_offset: -new Date().getTimezoneOffset() } }).then((r) => r.data)
  },
  listTopUps(params: { status?: string; user?: number; from?: string; to?: string; page: number; page_size: number }): Promise<{ items: TopUpOrder[]; total: number }> {
    return client.get('/admin/billing/topups', { params }).then((r) => r.data)
  },
  /** Asks Alipay about an order, and credits it when it was paid. */
  checkTopUp(id: number): Promise<OrderCheck> {
    return client.post(`/admin/billing/topups/${id}/check`).then((r) => r.data)
  },
  userAccount(id: number, page = 1): Promise<AccountView> {
    return client.get(`/admin/users/${id}/account`, { params: { page } }).then((r) => r.data)
  },
  adjustBalance(id: number, amountCents: number, note: string): Promise<LedgerEntry> {
    return client.post(`/admin/users/${id}/balance`, { amount_cents: amountCents, note }).then((r) => r.data)
  },
  // 财务管理: 财务设置, the summary, package orders and the review pages.
  financeSettings(): Promise<FinanceSettings> {
    return client.get('/admin/finance/settings').then((r) => r.data)
  },
  saveFinanceSettings(body: FinanceSettings): Promise<FinanceSettings> {
    return client.put('/admin/finance/settings', body).then((r) => r.data)
  },
  financeSummary(): Promise<FinanceSummary> {
    return client.get('/admin/finance/summary').then((r) => r.data)
  },
  listPackageOrders(params: FinanceQuery): FinancePage<PackageOrder> {
    return client.get('/admin/finance/orders', { params }).then((r) => r.data)
  },
  downloadFinanceFile(id: number): Promise<Blob> {
    return client.get(`/admin/finance/uploads/${id}`, { responseType: 'blob' }).then((r) => r.data)
  },
  listRemittances(params: FinanceQuery): FinancePage<Remittance> {
    return client.get('/admin/finance/remittances', { params }).then((r) => r.data)
  },
  reviewRemittance(id: number, approve: boolean, note: string): Promise<Remittance> {
    return client.post(`/admin/finance/remittances/${id}/${approve ? 'approve' : 'reject'}`, { note }).then((r) => r.data)
  },
  listWithdrawals(params: FinanceQuery): FinancePage<Withdrawal> {
    return client.get('/admin/finance/withdrawals', { params }).then((r) => r.data)
  },
  /** Sends a withdrawal — or retries a failed one — to Alipay. */
  approveWithdrawal(id: number, note: string): Promise<Withdrawal> {
    return client.post(`/admin/finance/withdrawals/${id}/approve`, { note }, { timeout: 60_000 }).then((r) => r.data)
  },
  rejectWithdrawal(id: number, note: string): Promise<Withdrawal> {
    return client.post(`/admin/finance/withdrawals/${id}/reject`, { note }).then((r) => r.data)
  },
  checkWithdrawal(id: number): Promise<Withdrawal> {
    return client.post(`/admin/finance/withdrawals/${id}/check`, undefined, { timeout: 60_000 }).then((r) => r.data)
  },
  listInvoices(params: FinanceQuery): FinancePage<Invoice> {
    return client.get('/admin/finance/invoices', { params }).then((r) => r.data)
  },
  uploadInvoiceFile(id: number, file: File): Promise<FinanceFile> {
    const form = new FormData()
    form.append('file', file)
    return client.post(`/admin/finance/invoices/${id}/file`, form, { timeout: 120_000 }).then((r) => r.data)
  },
  issueInvoice(id: number, body: { number: string; file_id: number; express_company: string; express_no: string; note: string }): Promise<Invoice> {
    return client.post(`/admin/finance/invoices/${id}/issue`, body).then((r) => r.data)
  },
  rejectInvoice(id: number, note: string): Promise<Invoice> {
    return client.post(`/admin/finance/invoices/${id}/reject`, { note }).then((r) => r.data)
  },
  listRefunds(params: FinanceQuery): FinancePage<Refund> {
    return client.get('/admin/finance/refunds', { params }).then((r) => r.data)
  },
  approveRefund(id: number, body: { approved_cents: number; revoke_time: boolean; note: string }): Promise<Refund> {
    return client.post(`/admin/finance/refunds/${id}/approve`, body, { timeout: 60_000 }).then((r) => r.data)
  },
  rejectRefund(id: number, note: string): Promise<Refund> {
    return client.post(`/admin/finance/refunds/${id}/reject`, { note }).then((r) => r.data)
  },
  checkRefund(id: number): Promise<Refund> {
    return client.post(`/admin/finance/refunds/${id}/check`, undefined, { timeout: 60_000 }).then((r) => r.data)
  },
  // 投诉管理.
  listComplaints(params: ComplaintQuery): FinancePage<Complaint> {
    return client.get('/admin/complaints', { params }).then((r) => r.data)
  },
  complaintSummary(): Promise<ComplaintSummary> {
    return client.get('/admin/complaints/summary').then((r) => r.data)
  },
  getComplaint(id: number): Promise<Complaint> {
    return client.get(`/admin/complaints/${id}`).then((r) => r.data)
  },
  acceptComplaint(id: number): Promise<Complaint> {
    return client.post(`/admin/complaints/${id}/accept`).then((r) => r.data)
  },
  resolveComplaint(id: number, result: string): Promise<Complaint> {
    return client.post(`/admin/complaints/${id}/resolve`, { result }).then((r) => r.data)
  },
  // 应用加速.
  listL4Rules(params: L4Query): FinancePage<L4Rule> {
    return client.get('/admin/l4-rules', { params }).then((r) => r.data)
  },
  l4Summary(): Promise<L4Summary> {
    return client.get('/admin/l4-rules/summary').then((r) => r.data)
  },
  setL4RuleEnabled(id: number, on: boolean): Promise<L4Rule> {
    return client.post(`/admin/l4-rules/${id}/${on ? 'enable' : 'disable'}`).then((r) => r.data)
  },
  deleteL4Rule(id: number): Promise<void> {
    return client.delete(`/admin/l4-rules/${id}`).then(() => undefined)
  },
  // One rule's statistics over a window, in the browser's time zone.
  l4RuleStats(id: number, range: import('@/utils/l4stats').L4Range): Promise<import('@/utils/l4stats').L4Report> {
    return client
      .get(`/admin/l4-rules/${id}/stats`, { params: { range, tz_offset: -new Date().getTimezoneOffset() } })
      .then((r) => r.data)
  },
  l4Settings(): Promise<L4Settings> {
    return client.get('/admin/l4-settings').then((r) => r.data)
  },
  saveL4Settings(body: L4Settings): Promise<L4Settings> {
    return client.put('/admin/l4-settings', body).then((r) => r.data)
  },
  // 工单管理.
  listTickets(params: TicketQuery): FinancePage<Ticket> {
    return client.get('/admin/tickets', { params }).then((r) => r.data)
  },
  ticketSummary(): Promise<StaffTicketSummary> {
    return client.get('/admin/tickets/summary').then((r) => r.data)
  },
  ticketStaff(): Promise<{ items: StaffMember[] }> {
    return client.get('/admin/tickets/staff').then((r) => r.data)
  },
  getTicket(id: number): Promise<TicketDetail> {
    return client.get(`/admin/tickets/${id}`).then((r) => r.data)
  },
  /** A reply to the customer, or with internal an internal note; close closes the ticket with the reply. */
  replyTicket(id: number, body: { body: string; format?: '' | 'html'; confidential?: boolean; attachment_ids: number[]; internal: boolean; close: boolean }): Promise<Ticket> {
    return client.post(`/admin/tickets/${id}/replies`, body).then((r) => r.data)
  },
  /** Gives the ticket to a staff member, or with null to nobody. */
  assignTicket(id: number, assigneeId: number | null): Promise<Ticket> {
    return client.post(`/admin/tickets/${id}/assign`, { assignee_id: assigneeId }).then((r) => r.data)
  },
  setTicketPriority(id: number, priority: TicketPriority): Promise<Ticket> {
    return client.post(`/admin/tickets/${id}/priority`, { priority }).then((r) => r.data)
  },
  acceptTicket(id: number): Promise<Ticket> {
    return client.post(`/admin/tickets/${id}/accept`).then((r) => r.data)
  },
  closeTicket(id: number): Promise<Ticket> {
    return client.post(`/admin/tickets/${id}/close`).then((r) => r.data)
  },
  uploadTicketFile(file: File, progress?: (pct: number) => void): Promise<TicketFile> {
    const form = new FormData()
    form.append('file', file)
    return client
      .post('/admin/tickets/uploads', form, {
        timeout: 120_000,
        onUploadProgress: (e) => progress?.(Math.min(99, Math.round((e.loaded * 100) / (e.total || file.size || 1)))),
      })
      .then((r) => r.data)
  },
  downloadTicketFile(id: number): Promise<Blob> {
    return client.get(`/admin/tickets/attachments/${id}`, { responseType: 'blob', timeout: 120_000 }).then((r) => r.data)
  },
  ticketTemplates(): Promise<{ items: TicketTemplate[] }> {
    return client.get('/admin/tickets/templates').then((r) => r.data)
  },
  saveTicketTemplate(id: number | null, body: { title: string; body: string }): Promise<TicketTemplate> {
    return (id ? client.put(`/admin/tickets/templates/${id}`, body) : client.post('/admin/tickets/templates', body)).then((r) => r.data)
  },
  deleteTicketTemplate(id: number) {
    return client.delete(`/admin/tickets/templates/${id}`).then((r) => r.data)
  },
  ticketSettings(): Promise<TicketSettings> {
    return client.get('/admin/tickets/settings').then((r) => r.data)
  },
  saveTicketSettings(body: TicketSettings): Promise<TicketSettings> {
    return client.put('/admin/tickets/settings', body).then((r) => r.data)
  },
  // 邮件服务.
  mailSettings(): Promise<MailView> {
    return client.get('/admin/mail').then((r) => r.data)
  },
  saveMailSettings(body: MailInput): Promise<MailView> {
    return client.put('/admin/mail', body).then((r) => r.data)
  },
  /** Sends a test mail now with the saved settings. */
  testMail(to: string): Promise<{ ok: boolean }> {
    return client.post('/admin/mail/test', { to }, { timeout: 60_000 }).then((r) => r.data)
  },
  // 短信服务.
  assistantSettings(): Promise<AssistantView> {
    return client.get('/admin/assistant').then((r) => r.data)
  },
  saveAssistantSettings(body: AssistantInput): Promise<AssistantView> {
    return client.put('/admin/assistant', body).then((r) => r.data)
  },
  testAssistant(): Promise<AssistantTestResult> {
    return client.post('/admin/assistant/test', {}, { timeout: 45_000 }).then((r) => r.data)
  },
  assistantUsage(days = 30): Promise<AssistantUsage> {
    return client.get('/admin/assistant/usage', { params: { days } }).then((r) => r.data)
  },
  // 实名认证.
  systemUpdate(): Promise<SystemUpdateView> {
    return client.get('/admin/system/update').then((r) => r.data)
  },
  checkSystemUpdate(): Promise<SystemUpdateView> {
    return client.post('/admin/system/update/check', null, { timeout: 60_000 }).then((r) => r.data)
  },
  applySystemUpdate(): Promise<SystemUpdateView> {
    return client.post('/admin/system/update/apply', null, { timeout: 60_000 }).then((r) => r.data)
  },
  saveSystemUpdateSettings(body: { auto_check: boolean }): Promise<SystemUpdateView> {
    return client.put('/admin/system/update/settings', body).then((r) => r.data)
  },
  upgradeNodes(body: { node_ids?: number[]; components?: ('agent' | 'edge')[] }): Promise<{ queued: number; skipped: number }> {
    return client.post('/admin/nodes/upgrade', body).then((r) => r.data)
  },
  nodeUpgrades(): Promise<{ items: NodeUpgrade[]; latest: Partial<Record<'agent' | 'edge', string>> | null }> {
    return client.get('/admin/nodes/upgrades').then((r) => r.data)
  },
  // 会员等级.
  memberSettings(): Promise<MemberSettings> {
    return client.get('/admin/member-settings').then((r) => r.data)
  },
  saveMemberSettings(body: MemberSettingsInput): Promise<MemberSettings> {
    return client.put('/admin/member-settings', body).then((r) => r.data)
  },
  /** A customer's 会员等级, and the platform's changes to it: amount up or down, with a note the customer sees. */
  memberAdmin(userID: number): Promise<MemberAdminView> {
    return client.get(`/admin/users/${userID}/member`).then((r) => r.data)
  },
  adjustExp(userID: number, amount: number, note: string): Promise<MemberAdminView> {
    return client.post(`/admin/users/${userID}/exp`, { amount, note }).then((r) => r.data)
  },
  adjustVipGrowth(userID: number, amount: number, note: string): Promise<MemberAdminView> {
    return client.post(`/admin/users/${userID}/vip-growth`, { amount, note }).then((r) => r.data)
  },
  realnameSettings(): Promise<RealnameAdminView> {
    return client.get('/admin/realname').then((r) => r.data)
  },
  saveRealnameSettings(body: RealnameSettingsInput): Promise<RealnameAdminView> {
    return client.put('/admin/realname', body).then((r) => r.data)
  },
  testRealname(provider: RealnameProvider): Promise<RealnameTest> {
    return client.post('/admin/realname/test', null, { params: { provider }, timeout: 30_000 }).then((r) => r.data)
  },
  realnameRecords(params: { account?: string; name?: string; method?: string; status?: string; page?: number; page_size?: number }) {
    return client.get('/admin/realname/records', { params }).then((r) => r.data as { items: RealnameRecord[]; total: number })
  },
  messageTemplates(): Promise<MessageTemplatesView> {
    return client.get('/admin/message-templates').then((r) => r.data)
  },
  saveMailTemplate(event: string, body: MailTemplate): Promise<MessageTemplatesView> {
    return client.put(`/admin/message-templates/mail/${event}`, body).then((r) => r.data)
  },
  saveInboxTemplate(event: string, body: InboxTemplate): Promise<MessageTemplatesView> {
    return client.put(`/admin/message-templates/inbox/${event}`, body).then((r) => r.data)
  },
  previewMailTemplate(event: string, body: MailTemplate): Promise<{ subject: string; html: string }> {
    return client.post(`/admin/message-templates/mail/${event}/preview`, body).then((r) => r.data)
  },
  saveSmsTemplate(scene: string, body: SMSTemplate): Promise<MessageTemplatesView> {
    return client.put(`/admin/message-templates/sms/${scene}`, body).then((r) => r.data)
  },
  /** Answers with 短信服务's page, which holds the 通用模板. */
  saveSmsDefaultTemplate(body: SMSDefaultTemplate): Promise<SMSView> {
    return client.put('/admin/message-templates/sms-default', body).then((r) => r.data)
  },
  smsSettings(): Promise<SMSView> {
    return client.get('/admin/sms').then((r) => r.data)
  },
  saveSmsSettings(body: SMSInput): Promise<SMSView> {
    return client.put('/admin/sms', body).then((r) => r.data)
  },
  testSms(phone: string): Promise<SentCode> {
    return client.post('/admin/sms/test', { phone }).then((r) => r.data)
  },
  smsRecords(params: { phone?: string; purpose?: string; status?: string; page?: number; page_size?: number }) {
    return client.get('/admin/sms/records', { params }).then((r) => r.data as { items: SMSRecord[]; total: number })
  },
  // ClickHouse page.
  clickHouse(): Promise<CHOverview> {
    return client.get('/admin/system/clickhouse').then((r) => r.data)
  },
  testClickHouse(body: CHConnInput): Promise<CHTestResult> {
    return client.post('/admin/system/clickhouse/test', body).then((r) => r.data)
  },
  saveClickHouse(body: CHConnInput & { init: boolean }): Promise<CHTestResult> {
    return client.put('/admin/system/clickhouse/connection', body).then((r) => r.data)
  },
  resetClickHouse() {
    return client.delete('/admin/system/clickhouse/connection').then((r) => r.data)
  },
  saveClickHousePolicy(body: { retention: Record<string, number>; cleanup: CHCleanup }): Promise<{ retention: Record<string, number>; cleanup: CHCleanup; run: CHRun | null }> {
    return client.put('/admin/system/clickhouse/policy', body).then((r) => r.data)
  },
  cleanClickHouse(): Promise<CHRun> {
    return client.post('/admin/system/clickhouse/cleanup').then((r) => r.data)
  },
  truncateClickHouseLogs(): Promise<CHRun> {
    return client.post('/admin/system/clickhouse/system-logs/truncate').then((r) => r.data)
  },
  initClickHouseSchema(): Promise<{ statements: number }> {
    return client.post('/admin/system/clickhouse/schema').then((r) => r.data)
  },
  // How far back the analytics pages can look: the access log's retention.
  analyticsLimits(): Promise<{ days: number; since: string }> {
    return client.get('/admin/analytics/limits').then((r) => r.data)
  },
  // Every access-control rule in force, each site's and the platform's:
  // what 访问统计 checks a key against before offering to block it.
  listActiveACL(): Promise<{ items: AclRule[] }> {
    return client.get('/admin/rules/acl').then((r) => r.data)
  },

  // License — platform/operator info, admin-only (see router.go's
  // comment on why this moved off the customer console). Activation and
  // renewal ask the portal: give them its time.
  license(): Promise<LicenseState> {
    return client.get('/license').then((r) => r.data)
  },
  activateLicense(code: string): Promise<LicenseState> {
    return client.post('/license/activate', { license_code: code }, { timeout: 60_000 }).then((r) => r.data)
  },
  uploadLicense(file: string): Promise<LicenseState> {
    return client.post('/license/offline', { file }).then((r) => r.data)
  },
  renewLicense(): Promise<LicenseState> {
    return client.post('/license/renew', null, { timeout: 60_000 }).then((r) => r.data)
  },
  deactivateLicense(): Promise<LicenseState> {
    return client.post('/license/deactivate', null, { timeout: 60_000 }).then((r) => r.data)
  },

  // Task queue — node-command tasks (see control/internal/service/tasks.go).
  // V1 whitelist: resync_nftables | reload_config.
  createNodeTask(nodeId: number, type: string, params: Record<string, unknown> = {}) {
    return client.post(`/admin/nodes/${nodeId}/tasks`, { type, params }).then((r) => r.data)
  },
  // One page of the queue, newest first, and the total the filters match.
  listTasks(params: {
    node_id?: number
    status?: string
    type?: string
    q?: string
    scope?: 'node' | 'system'
    page?: number
    page_size?: number
  } = {}): Promise<{ items: any[]; total: number }> {
    return client.get('/admin/tasks', { params }).then((r) => r.data)
  },
  // System tasks only: runs it again now with its attempt count reset.
  retryTask(id: number) {
    return client.post(`/admin/tasks/${id}/retry`).then((r) => r.data)
  },

  // 管理解析: one line group's resolve lines and the addresses in them.
  lineGroupDns(groupId: number) {
    return client.get(`/admin/node-groups/${groupId}/dns`).then((r) => r.data)
  },
  createLinePool(groupId: number, body: { line: string; display_name?: string }) {
    return client.post(`/admin/node-groups/${groupId}/pools`, body).then((r) => r.data)
  },
  setLinePoolEnabled(groupId: number, poolId: number, enabled: boolean) {
    return client.put(`/admin/node-groups/${groupId}/pools/${poolId}`, { enabled }).then((r) => r.data)
  },
  deleteLinePool(groupId: number, poolId: number) {
    return client.delete(`/admin/node-groups/${groupId}/pools/${poolId}`).then((r) => r.data)
  },
  addLinePoolMembers(groupId: number, poolId: number, members: { ip: string; node_id?: number; role?: string }[]) {
    return client.post(`/admin/node-groups/${groupId}/pools/${poolId}/members`, { members }).then((r) => r.data)
  },
  updateLinePoolMember(
    groupId: number,
    poolId: number,
    memberId: number,
    patch: { role?: string; enabled?: boolean; note?: string },
  ) {
    return client.patch(`/admin/node-groups/${groupId}/pools/${poolId}/members/${memberId}`, patch).then((r) => r.data)
  },
  deleteLinePoolMember(groupId: number, poolId: number, memberId: number) {
    return client.delete(`/admin/node-groups/${groupId}/pools/${poolId}/members/${memberId}`).then((r) => r.data)
  },

  // Publishes the line group's 解析值 as A records for its member nodes,
  // via the system task queue.
  publishLineResolve(groupId: number) {
    return client.post(`/admin/node-groups/${groupId}/publish-resolve`).then((r) => r.data)
  },

  // System settings — platform branding and the registration switch.
  systemSettings() {
    return client.get('/admin/system/settings').then((r) => r.data)
  },
  updateSystemSettings(body: Record<string, unknown>) {
    return client.put('/admin/system/settings', body).then((r) => r.data)
  },
  // The platform firewall policy: which of a banned address's traffic the
  // nodes' kernels drop. Saved policies come back normalised (sorted,
  // overlapping ranges merged) — what the nodes will actually run.
  firewallPolicy(): Promise<{ policy: FirewallPolicy; default: FirewallPolicy }> {
    return client.get('/admin/system/firewall').then((r) => r.data)
  },
  updateFirewallPolicy(body: FirewallPolicy): Promise<{ policy: FirewallPolicy }> {
    return client.put('/admin/system/firewall', body).then((r) => r.data)
  },
  // Node protection: per-address limits every node's edge applies before
  // any site is involved.
  // 主控域名.
  consoleTLS(): Promise<{ status: ConsoleTLSStatus; issue: Partial<Record<ConsoleSiteName, IssueState>>; default_https_port: number }> {
    return client.get('/admin/system/console-tls').then((r) => r.data)
  },
  updateConsoleTLS(body: ConsoleSettings): Promise<{ settings: ConsoleSettings }> {
    return client.put('/admin/system/console-tls', body).then((r) => r.data)
  },
  uploadConsoleCert(site: ConsoleSiteName, body: { cert_pem: string; key_pem: string }): Promise<{ cert: ControlCert }> {
    return client.post(`/admin/system/console-tls/${site}/cert`, body).then((r) => r.data)
  },
  removeConsoleCert(site: ConsoleSiteName) {
    return client.delete(`/admin/system/console-tls/${site}/cert`).then((r) => r.data)
  },
  issueConsoleCert(site: ConsoleSiteName): Promise<{ task_id: number }> {
    return client.post(`/admin/system/console-tls/${site}/issue`).then((r) => r.data)
  },
  cancelConsoleCertIssue(site: ConsoleSiteName) {
    return client.delete(`/admin/system/console-tls/${site}/issue`).then((r) => r.data)
  },
  // 主控通信.
  controlTLS(): Promise<{ status: ControlTLSStatus; issue?: IssueState; default_port: number }> {
    return client.get('/admin/system/control-tls').then((r) => r.data)
  },
  updateControlTLS(body: ControlTLSSettings & { force?: boolean }): Promise<{ settings: ControlTLSSettings }> {
    return client.put('/admin/system/control-tls', body).then((r) => r.data)
  },
  uploadControlCert(body: { cert_pem: string; key_pem: string }): Promise<{ cert: ControlCert }> {
    return client.post('/admin/system/control-tls/cert', body).then((r) => r.data)
  },
  removeControlCert() {
    return client.delete('/admin/system/control-tls/cert').then((r) => r.data)
  },
  issueControlCert(): Promise<{ task_id: number }> {
    return client.post('/admin/system/control-tls/issue').then((r) => r.data)
  },
  cancelControlCertIssue() {
    return client.delete('/admin/system/control-tls/issue').then((r) => r.data)
  },
  // 人机验证: the service and its keys, the operations that ask, a test of
  // the saved keys with a challenge the page just ran, the failure record.
  captchaSettings(): Promise<CaptchaView> {
    return client.get('/admin/captcha').then((r) => r.data)
  },
  saveCaptchaSettings(body: CaptchaInput): Promise<CaptchaView> {
    return client.put('/admin/captcha', body).then((r) => r.data)
  },
  testCaptcha(captcha: CaptchaAnswer): Promise<{ verdict: 'pass' | 'rejected' | 'unavailable'; detail?: string }> {
    return client.post('/admin/captcha/test', { captcha }).then((r) => r.data)
  },
  clearCaptchaState(): Promise<CaptchaView> {
    return client.delete('/admin/captcha/state').then((r) => r.data)
  },
  // 全网联动封禁: a kernel ban one node applies goes to every node.
  banSync(): Promise<{ sync: BanSync; default: BanSync }> {
    return client.get('/admin/system/ban-sync').then((r) => r.data)
  },
  updateBanSync(body: BanSync): Promise<{ sync: BanSync }> {
    return client.put('/admin/system/ban-sync', body).then((r) => r.data)
  },
  nodeProtection(): Promise<{ protection: NodeProtection; default: NodeProtection }> {
    return client.get('/admin/system/protection').then((r) => r.data)
  },
  updateNodeProtection(body: NodeProtection): Promise<{ protection: NodeProtection }> {
    return client.put('/admin/system/protection', body).then((r) => r.data)
  },
  // Default pages: what the edge answers with when it answers a request
  // itself. The catalog carries each page's built-in text and template.
  defaultPages(): Promise<{
    config: DefaultPagesConfig
    catalog: DefaultPageDef[]
    placeholders: string[]
    limits: { title: number; message: number; html: number; grace_hours: number }
  }> {
    return client.get('/admin/system/pages').then((r) => r.data)
  },
  updateDefaultPages(body: DefaultPagesConfig): Promise<{ config: DefaultPagesConfig }> {
    return client.put('/admin/system/pages', body).then((r) => r.data)
  },
  // Renders one page with sample values, validated as a save would be.
  previewDefaultPage(key: string, page: DefaultPage): Promise<{ html: string }> {
    return client.post('/admin/system/pages/preview', { key, page }).then((r) => r.data)
  },
  // The per-node ceiling on concurrent origin requests. Its own endpoint
  // rather than part of updateDomain, which assigns the protocol switches
  // unconditionally and would turn HTTP/2 and friends off.
  // Binds a site to one of its owner's held packages, or null for none.
  setDomainPackage(domainId: number, packageId: number | null) {
    return client.put(`/admin/domains/${domainId}/package`, { package_id: packageId }).then((r) => r.data)
  },
  updateUpstreamLimit(domainId: number, max: number) {
    return client
      .put(`/admin/domains/${domainId}/upstream-limit`, { upstream_max_concurrent: max })
      .then((r) => r.data)
  },

  // Cache purge and prefetch. Accepted, not completed: the job fans out
  // to every node and finishes when they have all reported.
  submitCacheJob(
    domainId: number,
    body: { kind: string; targets: string[]; scheduled_at?: string },
  ) {
    return client.post(`/admin/domains/${domainId}/cache/jobs`, body).then((r) => r.data)
  },
  listCacheJobs(params: { domain_id?: number; kind?: string; limit?: number } = {}) {
    return client.get('/admin/cache/jobs', { params }).then((r) => r.data)
  },

  // IP lists. scope="system" is the platform-wide list; scope="user"
  // with a user_id lets an operator manage one customer's list.
  listIPEntries(params: { scope?: string; user_id?: number; q?: string } = {}) {
    return client.get('/admin/ip-lists', { params }).then((r) => r.data)
  },
  addIPEntry(body: { kind: string; target: string; note?: string; enforcement?: string },
             params: { scope?: string; user_id?: number } = {}) {
    return client.post('/admin/ip-lists', body, { params }).then((r) => r.data)
  },
  // One request rather than a loop: every single-entry add schedules a
  // republish for every owner with sites, so N requests means N rounds of
  // that fan-out.
  addIPEntriesBatch(
    body: { kind: string; targets: string[]; note?: string; enforcement?: string },
    params: { scope?: string; user_id?: number } = {},
  ) {
    return client.post('/admin/ip-lists/batch', body, { params }).then((r) => r.data)
  },
  deleteIPEntry(id: number, params: { scope?: string; user_id?: number } = {}) {
    return client.delete(`/admin/ip-lists/${id}`, { params }).then((r) => r.data)
  },
  listAutoBans(params: { state?: string; q?: string; ip?: string; node_id?: number } = {}) {
    return client.get('/admin/ip-auto-bans', { params }).then((r) => r.data)
  },
  releaseAutoBan(id: number) {
    return client.post(`/admin/ip-auto-bans/${id}/release`).then((r) => r.data)
  },

  uploadFavicon(file: File) {
    const form = new FormData()
    form.append('file', file)
    // No explicit Content-Type: the browser has to set it so the
    // multipart boundary is included, and naming it by hand omits that.
    return client.post('/admin/system/favicon', form).then((r) => r.data)
  },
  // The logo beside the name (PNG, JPG, WebP, GIF, or an SVG the control plane rebuilds); removed, the favicon stands in.
  /** One of the four logos: logo, logo_dark, logo_mini, logo_mini_dark. */
  uploadLogo(file: File, slot = 'logo') {
    const form = new FormData()
    form.append('file', file)
    return client.post(`/admin/system/logo/${slot}`, form).then((r) => r.data)
  },
  deleteLogo(slot = 'logo') {
    return client.delete(`/admin/system/logo/${slot}`)
  },
  /** The login pages' backgrounds: auth_bg (light) and auth_bg_dark. */
  uploadBackground(file: File, slot: string) {
    const form = new FormData()
    form.append('file', file)
    return client.post(`/admin/system/background/${slot}`, form).then((r) => r.data)
  },
  deleteBackground(slot: string) {
    return client.delete(`/admin/system/background/${slot}`)
  },
}

export default client
